import React from 'react';
import styled from 'styled-components';

interface IProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  variant?: 'filled' | 'outlined';
  label?: string;
  children?: React.ReactNode;
  wrapperStyle?: React.CSSProperties;
  /* Select 너비 (기본값: outlined일 때 148px, 그 외 100%) */
  width?: string | number;
}

const SelectWrapper = styled.div<{ $width?: string | number }>`
  position: relative;
  display: inline-block;
  width: ${({ $width }) => {
    if ($width === undefined) return '100%';
    return typeof $width === 'number' ? `${$width}px` : $width;
  }};
`;

const Label = styled.label`
  position: absolute;
  top: -19px;
  left: 0px;
  z-index: 1;
  white-space: nowrap;
  color: #545454;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const HiddenSelect = styled.select`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;

  @media (hover: none) and (pointer: coarse) {
    position: relative;
    opacity: 1;
    pointer-events: auto;
  }
`;

const SelectButton = styled.button<{ $variant?: 'filled' | 'outlined'; $isOpen?: boolean }>`
  ${({ $variant }) => {
    switch ($variant) {
      case 'outlined':
        return `
          height: 35px;
          padding: 2px 36px 2px 14px;
          border-radius: 6px;
          border: 1px solid #D8DDE3;
          background: #FFF;
          color: #333;
          font-family: Pretendard;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: 31px;
        `;
      case 'filled':
      default:
        return `
          height: 35px;
          padding: 5px 30px 5px 8px;
          background: none;
          border: none;
          border-bottom: 1px solid #222;
          font-size: 14px;
          font-weight: 500;
        `;
    }
  }}

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  text-align: left;

  &:after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    width: 12px;
    height: 12px;
    transform: translateY(-50%) ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%23707A8A' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }

  &:disabled {
    cursor: not-allowed;
    color: #9aa4b2;
    border-color: #e3e6eb;
    background: #f7f8fa;
  }

  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const SelectedText = styled.span<{ $variant?: 'filled' | 'outlined' }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ $variant }) => ($variant === 'outlined' ? '14px' : '18px')};
`;

const Dropdown = styled.ul<{ $variant?: 'filled' | 'outlined'; $isOpen?: boolean }>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  max-height: 240px;
  margin: 0;
  padding: 8px 0;
  list-style: none;
  border-radius: 8px;
  border: 1px solid #e1e5eb;
  background: #fff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
  overflow-y: auto;
  z-index: 999;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};

  /* scrollbar styling (desktop) */
  scrollbar-width: thin;
  scrollbar-color: #c7ccd4 transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c7ccd4;
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #aeb5bf;
  }

  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const OptionItem = styled.li<{ $isSelected?: boolean; $isHighlighted?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 14px;
  color: ${({ $disabled }) => ($disabled ? '#b0b8c4' : '#333')};
  background: ${({ $isSelected, $isHighlighted }) => {
    if ($isHighlighted) return '#EEF4FF';
    if ($isSelected) return '#F3F6FA';
    return 'transparent';
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 500)};
`;

const Select = React.forwardRef<HTMLSelectElement, IProps>(
  ({ label, variant = 'filled', children, style, wrapperStyle, width, value, defaultValue, onChange, onBlur, disabled, ...props }, ref) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const selectRef = React.useRef<HTMLSelectElement | null>(null) as React.MutableRefObject<HTMLSelectElement | null>;

    const setSelectRef = React.useCallback(
      (node: HTMLSelectElement | null) => {
        selectRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLSelectElement | null>).current = node;
        }
      },
      [ref],
    );

    const options = React.useMemo(() => {
      const collected: Array<{ value: string; label: React.ReactNode; disabled?: boolean }> = [];

      const collectOptions = (node: React.ReactNode) => {
        React.Children.forEach(node, (child) => {
          if (!React.isValidElement(child)) return;
          if (child.type === 'option') {
            const optionValue = child.props.value !== undefined ? String(child.props.value) : String(child.props.children ?? '');
            collected.push({
              value: optionValue,
              label: child.props.children,
              disabled: child.props.disabled,
            });
            return;
          }

          if (child.type === 'optgroup') {
            collectOptions(child.props.children);
          }
        });
      };

      collectOptions(children);
      return collected;
    }, [children]);

    const isControlled = value !== undefined;
    const styleWidth = style?.width;
    const computedWidth = width ?? styleWidth ?? (variant === 'outlined' ? 148 : '100%');
    const initialUncontrolledValue = (defaultValue as string | undefined) ?? (options.length > 0 ? options[0].value : '');
    const [internalValue, setInternalValue] = React.useState<string>(String(initialUncontrolledValue ?? ''));
    const [isOpen, setIsOpen] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

    const selectedValue = isControlled ? String(value ?? '') : internalValue;
    const selectedOption = options.find((option) => option.value === selectedValue) ?? options[0] ?? { value: '', label: '' };

    React.useEffect(() => {
      if (isControlled) return;
      if (options.length === 0) return;
      if (options.some((option) => option.value === internalValue)) return;
      setInternalValue(options[0].value);
    }, [isControlled, internalValue, options]);

    React.useEffect(() => {
      if (!isOpen) return;
      const selectedIndex = options.findIndex((option) => option.value === selectedValue);
      setHighlightedIndex(selectedIndex);
    }, [isOpen, options, selectedValue]);

    const emitBlur = React.useCallback(() => {
      if (!onBlur) return;
      const event = {
        target: selectRef.current,
        currentTarget: selectRef.current,
        type: 'blur',
      } as unknown as React.FocusEvent<HTMLSelectElement>;
      onBlur(event);
    }, [onBlur]);

    React.useEffect(() => {
      const handleOutside = (event: MouseEvent | TouchEvent) => {
        if (!wrapperRef.current) return;
        if (!wrapperRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          emitBlur();
        }
      };

      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside);
      return () => {
        document.removeEventListener('mousedown', handleOutside);
        document.removeEventListener('touchstart', handleOutside);
      };
    }, [emitBlur]);

    const emitChange = React.useCallback(
      (nextValue: string) => {
        if (selectRef.current) {
          selectRef.current.value = nextValue;
          const event = new Event('change', { bubbles: true });
          selectRef.current.dispatchEvent(event);
        }

        if (!isControlled) {
          setInternalValue(nextValue);
        }
      },
      [isControlled],
    );

    const handleSelect = (optionValue: string, optionDisabled?: boolean) => {
      if (disabled || optionDisabled) return;
      emitChange(optionValue);
      setIsOpen(false);
      emitBlur();
    };

    const getNextEnabledIndex = React.useCallback(
      (startIndex: number, step: number) => {
        if (options.length === 0) return -1;
        let index = startIndex;
        while (index >= 0 && index < options.length) {
          if (!options[index].disabled) return index;
          index += step;
        }
        return -1;
      },
      [options],
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const option = options[highlightedIndex];
          if (option) handleSelect(option.value, option.disabled);
          return;
        }
        setIsOpen((prev) => !prev);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((prev) => {
          const next = getNextEnabledIndex(prev < 0 ? 0 : prev + 1, 1);
          return next === -1 ? prev : next;
        });
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((prev) => {
          const next = getNextEnabledIndex(prev < 0 ? options.length - 1 : prev - 1, -1);
          return next === -1 ? prev : next;
        });
        return;
      }

      if (event.key === 'Escape') {
        setIsOpen(false);
        emitBlur();
      }
    };

    const handleListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
      if (disabled) return;
      if (event.key === 'Enter' && highlightedIndex >= 0) {
        event.preventDefault();
        const option = options[highlightedIndex];
        if (option) handleSelect(option.value, option.disabled);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    return (
      <SelectWrapper ref={wrapperRef} style={wrapperStyle} $width={computedWidth}>
        {label && <Label>{label}</Label>}
        <SelectButton
          type="button"
          $variant={variant}
          $isOpen={isOpen}
          style={style}
          onClick={() => {
            if (disabled) return;
            setIsOpen((prev) => !prev);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <SelectedText $variant={variant}>{selectedOption.label}</SelectedText>
        </SelectButton>
        <HiddenSelect ref={setSelectRef} value={selectedValue} onChange={onChange} onBlur={onBlur} disabled={disabled} {...props}>
          {children}
        </HiddenSelect>
        <Dropdown role="listbox" $variant={variant} $isOpen={isOpen} onKeyDown={handleListKeyDown} tabIndex={-1}>
          {options.map((option, index) => (
            <OptionItem
              key={`${option.value}-${index}`}
              role="option"
              aria-selected={option.value === selectedValue}
              $isSelected={option.value === selectedValue}
              $isHighlighted={index === highlightedIndex}
              $disabled={option.disabled}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => handleSelect(option.value, option.disabled)}
            >
              {option.label}
            </OptionItem>
          ))}
        </Dropdown>
      </SelectWrapper>
    );
  },
);

Select.displayName = 'Select';

export default Select;
